/**
 * 単元内ステージ(公文式ドリル。docs/13 §11)の区切りと引き当て。
 *
 * **ステージは単元の問題プールを「現行の並び順のまま」10問前後ずつ均等に切ったもの**。
 * 並べ替えも抽選もしない —— ここが公文のプリントとの約束で、
 * 同じステージを何度やっても同じ順で同じ問題が出る。
 *
 * ビルド(`tools/content-build`)がこの関数で切って配信JSONに焼き込むので、
 * 実行時は基本的に `unit.stages` をそのまま読むだけ。ただし
 * **ステージ制より前に配られた JSON**(古いキャッシュ)にも耐えられるよう、
 * `quizUnitStages()` は無ければその場で同じ規則で導出する
 * (= 導出とビルドの結果は必ず一致する。テストで縛ってある)。
 */

import { QUIZ_STAGE } from './config.js';
import {
  DEFAULT_QUIZ_PRESENTATION,
  type QuizPresentation,
  type QuizStage,
  type QuizUnit,
} from './types.js';

/** ステージIDの形(`{単元ID}-s{1始まりの番号}`)。 */
export const QUIZ_STAGE_ID_PATTERN = /^(.+)-s([1-9]\d*)$/;

/** 単元IDと番号(1始まり)からステージIDを作る。 */
export function quizStageId(unitId: string, number: number): string {
  return `${unitId}-s${number}`;
}

/** ステージIDを単元IDと番号に分解する(形が違えば `null`)。 */
export function parseQuizStageId(
  stageId: string,
): { readonly unitId: string; readonly number: number } | null {
  const match = QUIZ_STAGE_ID_PATTERN.exec(stageId);
  if (!match?.[1] || !match[2]) return null;
  return { unitId: match[1], number: Number(match[2]) };
}

/** その単元の1つめのステージのID(既存の単元星を lazy 移行する先)。 */
export function firstQuizStageId(unitId: string): string {
  return quizStageId(unitId, 1);
}

/**
 * 問題の並びをステージへ**均等に**区切る(docs/13 §11。確定 2026-08-22)。
 *
 * 規則はこれだけ:
 *   1. `QUIZ_STAGE.singleStageMax`(12)問までの単元は**1ステージ**
 *   2. それ以上は `ceil(問題数 / QUIZ_STAGE.questions)` 個のステージに分け、
 *      問数の差が**1以内**になるよう均等に配る(余りは前のステージから1問ずつ)
 *
 * 例: 16問 → 8+8 / 17問 → 9+8 / 23問 → 8+8+7 / 98問 → 10×8 + 9+9。
 *
 * 「10問ずつ切って端数を最後へ寄せる」旧規則をやめたのは、16〜17問のステージができて
 * 1回が長すぎたため。**均等割りなら1回の量がどの単元でもほぼ同じ**になる。
 *
 * 並べ替えはしない ―― 結果を順につなぐと必ず元の並びに戻る(検証もこの性質を見る)。
 */
export function splitQuizStages(
  unitId: string,
  questionIds: readonly string[],
  presentation: QuizPresentation = DEFAULT_QUIZ_PRESENTATION,
): QuizStage[] {
  const total = questionIds.length;
  if (total === 0) return [];
  const count = total <= QUIZ_STAGE.singleStageMax ? 1 : Math.ceil(total / QUIZ_STAGE.questions);
  /** 全ステージ共通の問数と、1問ずつ多く持つステージの数。 */
  const base = Math.floor(total / count);
  const extra = total % count;

  const stages: QuizStage[] = [];
  let at = 0;
  for (let index = 0; index < count; index += 1) {
    // 1問多いステージは前へ寄せる(あとのステージほど軽く = 最後まで走りきりやすい)。
    const size = base + (index < extra ? 1 : 0);
    stages.push({
      id: quizStageId(unitId, index + 1),
      questionIds: [...questionIds.slice(at, at + size)],
      presentation,
    });
    at += size;
  }
  return stages;
}

/**
 * 単元のステージ(配信データにあればそれ、無ければ同じ規則で導出)。
 *
 * 呼び出し側は「ステージがある単元 / ない単元」を気にしなくてよい。
 */
export function quizUnitStages(unit: QuizUnit): readonly QuizStage[] {
  if (unit.stages && unit.stages.length > 0) return unit.stages;
  return splitQuizStages(unit.id, unit.questionIds);
}

/** 単元の中でのステージの位置(0始まり。無ければ -1)。 */
export function quizStageIndex(unit: QuizUnit, stageId: string): number {
  return quizUnitStages(unit).findIndex((stage) => stage.id === stageId);
}

/** ステージを引く(無ければ `undefined`)。 */
export function findQuizStage(unit: QuizUnit, stageId: string): QuizStage | undefined {
  return quizUnitStages(unit).find((stage) => stage.id === stageId);
}

/** その単元のステージ数。 */
export function quizStageCount(unit: QuizUnit): number {
  return quizUnitStages(unit).length;
}
